import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAuthenticator } from '@aws-amplify/ui-react';

export type UserRole = 'admin' | 'sub-admin' | 'supervisor' | 'transcriber' | 'enumerator';

interface UserContextValue {
  role: UserRole | null;
  userId: string | null;
  isAdmin: boolean;
  isSubAdmin: boolean;
  isLoading: boolean;
}

const UserContext = createContext<UserContextValue>({
  role: null,
  userId: null,
  isAdmin: false,
  isSubAdmin: false,
  isLoading: true,
});

export function UserProvider({ children }: { children: ReactNode }) {
  const { authStatus } = useAuthenticator();
  const [role, setRole] = useState<UserRole | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (authStatus === 'authenticated') {
      setIsLoading(true);
      fetchAuthSession()
        .then((session) => {
          const payload = session.tokens?.idToken?.payload ?? {};
          const groups = (payload['cognito:groups'] as string[]) ?? [];
          if (groups.includes('Administrators')) setRole('admin');
          else if (groups.includes('SubAdministrators')) setRole('sub-admin');
          else if (groups.includes('Supervisors')) setRole('supervisor');
          else if (groups.includes('Transcribers')) setRole('transcriber');
          else setRole('enumerator');
          setUserId(((payload.sub as string) ?? null));
        })
        .catch(() => setRole('enumerator'))
        .finally(() => setIsLoading(false));
    } else if (authStatus === 'unauthenticated') {
      setRole(null);
      setUserId(null);
      setIsLoading(false);
    }
  }, [authStatus]);

  return (
    <UserContext.Provider
      value={{
        role,
        userId,
        isAdmin: role === 'admin',
        isSubAdmin: role === 'sub-admin',
        isLoading,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export const useCurrentUser = () => useContext(UserContext);
